import React, { FC, Fragment } from 'react';
import { InputAdornment, TextField, TextFieldProps, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

type DataTableTextFieldProps = TextFieldProps & {
    selected?: boolean;
    readonly?: boolean;
    alignRight?: boolean;
    tooltipText?: string;
};

const getBackgroundColor = (props: DataTableTextFieldProps): string => {
    if (props.error) return '#fdecea';
    if (props.readonly) return '#f7f7f7';
    if (props.selected) return '#e9f5f3';
    return 'transparent';
};

const DataTableTextField: FC<DataTableTextFieldProps> = (props) => {
    const { selected, readonly, alignRight, error, tooltipText, ...propsToTextField } = props;

    const styleProps = {
        '& .MuiInputBase-root': {
            padding: 0,
            paddingRight: '3px',
            fontSize: '0.9rem',
            backgroundColor: getBackgroundColor(props),
        },
        '& .MuiInputBase-input': {
            padding: '4px 8px',
            borderRadius: 4,
            textAlign: alignRight ? 'right' : 'left',
        },
        '& .MuiInput-underline:before, & .MuiInput-underline:after': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
        },
        '& input': {
            cursor: readonly ? 'default' : 'text',
        },
        ...props.sx,
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            // find the closest table
            const table = (event.currentTarget as HTMLElement).closest('table');
            if (!table) return;

            // get all inputs, text-areas, selects inside the table
            const focusable = Array.from(
                table.querySelectorAll<HTMLElement>("input, select, textarea, [tabindex]:not([tabindex='-1'])"),
            ).filter((el) => !el.hasAttribute('disabled'));

            // find current active element index
            const index = focusable.indexOf(document.activeElement as HTMLElement);
            const next = focusable[index + 1];

            if (next) {
                next.focus();
            }
        }
    };

    return (
        <Fragment>
            <Tooltip title={tooltipText} placement="right" arrow>
                <TextField
                    {...propsToTextField}
                    autoComplete="off"
                    sx={styleProps}
                    onKeyUp={handleKeyUp}
                    inputProps={{ ...propsToTextField.inputProps, autoComplete: 'off' }}
                    InputProps={{
                        ...propsToTextField.InputProps,
                        readOnly: readonly,
                        ...(tooltipText && {
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    sx={{ fontSize: '12px', marginLeft: '4px', marginRight: '1px' }}
                                >
                                    <InfoIcon fontSize="inherit" color="info" />
                                </InputAdornment>
                            ),
                        }),
                    }}
                />
            </Tooltip>
        </Fragment>
    );
};

export default DataTableTextField;
